import React, {
  FC,
  ChangeEvent,
  useState,
  useEffect,
  ClipboardEvent,
  useRef
} from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';

import {
  AutocompleteDropdown,
  CustomModal,
  RhfTextField,
  RhfTextFieldWithPattern
} from '@components';
import {
  SHORT_FIELD_MAX_LENGTH,
  CIN_FIELD_MAX_LENGTH,
  REGISTRATION_NUMBER_FIELD_MAX_LENGTH,
  alphaNumericRegex,
  API_RESPONSE_STATUS
} from '@constants/common';
import { translate } from '@utils/locale';
import { ToastTypes } from '@type/toast';
import { extractAlphanumeric } from '@utils/common';
import { Country, Currency, TimeZone } from '@type/util';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';
import FormDualButton from '@components/form-dual-button/FormDualButton';

import { SectionMapper } from '../constants';
import { useUpdateCompanyMutation } from '../api';
import { Company } from '../types';
import { formatUpdateCompanyDealDataForApiCall } from '../services';

const LegalInfoSection: FC<{
  editMode?: SectionMapper;
  updateEditMode?: (section?: SectionMapper) => void;
  disableEdit?: boolean;
  companyId: string;
  currencyList: Currency[] | undefined;
  countriesList: Country[] | undefined;
  timeZoneList: TimeZone[] | undefined;
  companyDetails: Company;
  getResetLegalInfoSection: (formValues: FieldValues) => {
    companyRegisteredName: string;
    cin: string;
    registrationNumber: string;
    incorporationCountry: string;
    currency: string;
  };
  getResetFunction: (
    editMode?: SectionMapper
  ) => (formValues: FieldValues) => {};
}> = ({
  editMode,
  updateEditMode,
  disableEdit,
  companyId,
  countriesList,
  currencyList,
  companyDetails,
  getResetLegalInfoSection,
  getResetFunction
}) => {
  const {
    control,
    setValue,
    clearErrors,
    getValues,
    trigger,
    reset,
    setFocus,
    formState: { errors }
  } = useFormContext();

  const selectedIncorporationCountryId = useWatch({
    name: 'incorporationCountry'
  });

  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();

  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();

  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);

  const isEditMode = editMode === SectionMapper.legalInfo;

  const countryValues =
    countriesList?.map(val => ({ id: val.iso3, name: val.name })) ?? [];

  const currencyValues =
    currencyList?.map(val => ({
      id: val.code,
      name: `${val.code} ${val.name} (${val.symbol})`
    })) ?? [];

  const companyRegisteredNameRef = useRef<HTMLInputElement | null>(null);

  setFocus('companyRegisteredName');

  const onCompanyRegisteredNameTextFieldClick = () =>
    clearErrors('companyRegisteredName');

  const onCinChange = (newValue: string) => {
    setValue('CIN', newValue);
  };

  const onRegistrationNumberChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    clearErrors('registrationNumber');
    const val = event.target.value;
    setValue(
      'registrationNumber',
      val.match(alphaNumericRegex) ? val : val.slice(0, -1)
    );
  };

  const onPasteRegistrationNumber = (
    event: ClipboardEvent<HTMLInputElement>
  ) => {
    const clipboardData = event.clipboardData
      .getData('text')
      .trimEnd()
      .replaceAll(' ', '')
      .substring(0, REGISTRATION_NUMBER_FIELD_MAX_LENGTH);
    setValue(
      'registrationNumber',
      clipboardData.match(alphaNumericRegex)
        ? clipboardData
        : getValues('registrationNumber')
    );
  };

  const onEditSaveButtonClick = async () => {
    if (!editMode) {
      updateEditMode?.(SectionMapper.legalInfo);
      return;
    }
    if (editMode !== SectionMapper.legalInfo) {
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger([
      'companyRegisteredName',
      'cin',
      'registrationNumber',
      'incorporationCountry',
      'currency'
    ]);
    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const onUpdateDetails = async () => {
    const values = getValues();
    try {
      const response = await updateCompany(
        formatUpdateCompanyDealDataForApiCall(companyId, companyDetails, {
          registered_name: values.companyRegisteredName?.trim(),
          cin: values.cin,
          registration_number: values.registrationNumber,
          incorporated_in: values.incorporationCountry,
          currency: values.currency || null
        })
      ).unwrap();
      if (response && response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Legal Info Updated Successfully');
        updateEditMode?.();
        setConfirmModalOpen(undefined);
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to Update Legal Info'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.legalInfo);
    setConfirmModalOpen(undefined);
  };

  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };

  useEffect(() => {
    companyRegisteredNameRef.current?.focus();
  }, [editMode]);

  useEffect(() => {
    reset(getResetLegalInfoSection(getValues()));
  }, [companyDetails]);

  return (
    <>
      <div className="flex flex-row justify-between items-center">
        <div className="text-xl font-semibold">
          {translate('company.legalInfoSection.title')}
        </div>
        <FormDualButton
          enableEdit={!disableEdit}
          showCancelOrSaveButton={isEditMode}
          onClickCancelButton={onClickCancelButton}
          onClickEditButton={onEditSaveButtonClick}
        />
      </div>

      <div className="w-full lg:w-[70%]">
        <div className="flex flex-row mr-6">
          <RhfTextField
            name="companyRegisteredName"
            label={translate('company.legalInfoSection.companyRegisteredName')}
            placeholder={translate(
              'company.placeholders.companyRegisteredName'
            )}
            control={control}
            errors={errors}
            wrapperClass="pt-4 mr-6 w-full"
            onClickTextField={onCompanyRegisteredNameTextFieldClick}
            maxLength={SHORT_FIELD_MAX_LENGTH}
            description={translate('company.description.companyRegisteredName')}
            isDisabled={!isEditMode}
            fieldRef={companyRegisteredNameRef}
            showCharacterLimit
          />
          <RhfTextFieldWithPattern
            name="cin"
            pattern={'U-99999-XX-9999-XXX-XXXXXX'}
            replaceCharacters={['U', '9', 'X']}
            label={translate('company.legalInfoSection.cin')}
            placeholder={translate('company.placeholders.uniqueId')}
            control={control}
            errors={errors}
            wrapperClass="pt-4 w-full"
            onInputChange={onCinChange}
            maxLength={CIN_FIELD_MAX_LENGTH}
            description={translate('company.description.cin')}
            isDisabled
            inputValidation={(val: string) => !!val.match(alphaNumericRegex)}
            defaultValue={extractAlphanumeric(getValues('cin'))}
          />
        </div>
        <div className="flex flex-row mr-6">
          <RhfTextField
            name="registrationNumber"
            label={translate('company.legalInfoSection.registrationNumber')}
            placeholder={translate('company.placeholders.registrationNumber')}
            control={control}
            errors={errors}
            wrapperClass="pt-6 mr-6 w-full"
            onChange={onRegistrationNumberChange}
            onPaste={onPasteRegistrationNumber}
            maxLength={REGISTRATION_NUMBER_FIELD_MAX_LENGTH}
            showCharacterLimit
            description={translate('company.description.registrationNumber')}
            isDisabled={!isEditMode}
          />
          <AutocompleteDropdown
            control={control}
            name="incorporationCountry"
            options={countryValues}
            errors={errors}
            selectedItem={countryValues.find(
              value => value.id === selectedIncorporationCountryId
            )}
            placeholder={translate(
              'company.legalInfoSection.incorporationCountry'
            )}
            wrapperClass="pt-6 w-full"
            description={translate('company.description.incorporationCountry')}
            isDisabled={!isEditMode}
            onChangeHandler={() => clearErrors('incorporationCountry')}
          />
        </div>
        <div className="flex flex-row mr-6 w-[50%]">
          <AutocompleteDropdown
            control={control}
            name="currency"
            options={currencyValues}
            errors={errors}
            selectedItem={currencyValues.find(
              value => value.id === getValues('currency')
            )}
            placeholder={translate('company.legalInfoSection.currency')}
            wrapperClass="pt-6 mr-6 w-full"
            isDisabled={!isEditMode}
          />
        </div>
      </div>

      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={isLoading}
        />
      )}
    </>
  );
};

export default LegalInfoSection;
